import {
  COMPONENT_TYPE,
  CONDITIONAL_FORM,
  GE_ID_TAG_PROPERTIES,
  SECTION,
  SUB_SECTION,
} from "./Constant";
import FormBuilderService from "../services/form-builder.service";

export const BASE64_PATTERN =
  /^(?:[A-Za-z0-9+/]{4})*(?:[A-Za-z0-9+/]{2}==|[A-Za-z0-9+/]{3}=)?$/;
export const TRANSLATION_KEYS_FILTER_LIST = [
  "translationFromCode",
  "translationToCode",
  "formLanguageCode",
];

const formBuilderService = new FormBuilderService();

const computeBase64Labels = (isBase64Converter, label) => {
  if (isBase64Converter) {
    if (!BASE64_PATTERN.test(label)) {
      //Encode the string to a byte array (UTF-8)
      const encoder = new TextEncoder();
      const byteArray = encoder.encode(label);
      //Convert the byte array to a base64 string
      return btoa(String.fromCharCode(...byteArray));
    } else {
      return label;
    }
  } else {
    if (BASE64_PATTERN.test(label)) {
      const decodedBase64 = atob(label);
      const decoder = new TextDecoder();
      return decoder.decode(
        new Uint8Array(decodedBase64.split("").map((c) => c.charCodeAt(0))),
      );
    } else {
      return label;
    }
  }
};

export const decodeBase64Label = (label) =>
  typeof label === "string" ? computeBase64Labels(false, label) : label;

export const htmlBase64Utils = (form, isBase64Converter) => {
  form?.children?.forEach((parent, parentIdx) => {
    parent?.children?.forEach((childs, childsIdx) => {
      if (childs?.buttonName !== "Subsection") {
        childs.label = computeBase64Labels(isBase64Converter, childs?.label);
        if (childs?.helpImageText) {
          childs.helpImageText = computeBase64Labels(
            isBase64Converter,
            childs?.helpImageText,
          );
        }
      } else if (childs?.buttonName === "Subsection") {
        childs?.children?.forEach((child, childIdx) => {
          child.label = computeBase64Labels(isBase64Converter, child?.label);
          if (child?.helpImageText) {
            child.helpImageText = computeBase64Labels(
              isBase64Converter,
              child?.helpImageText,
            );
          }
        });
      }
    });
  });
};

export const handleSiteResponseAndSorting = (siteResponse) => {
  return siteResponse
    .sort((a, b) => a.properties.name.localeCompare(b.properties.name))
    .map(({ siteId, properties: { name } }) => {
      return {
        id: siteId,
        label: name,
      };
    });
};

export const toCapitalizeFirstLetterTitle = (item) => {
  if (!item) {
    return item;
  }
  return item.charAt(0).toUpperCase() + item.slice(1).toLowerCase();
};

export const toSetDefaultEditorBreakTag = (item) => {
  if (!item) {
    return item;
  }
  // Replace newlines with <br /> tags
  let formattedItem = item.replace(/\n/g, "<br />");

  // Ensure <br /> tags are consistent and remove any extra spaces or malformed tags
  formattedItem = formattedItem.replace(/<br\s*\/?>(<\/br>)?/g, "<br />");

  return formattedItem;
};

export const htmlToPlainText = (html) => {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = html;
  return tempDiv.textContent;
};

export const getIdFromString = (str) => {
  const idStart = str?.indexOf("id=") + 3;
  const idEnd = str?.indexOf(",");
  const id = str.substring(idStart, idEnd).trim();
  return id;
};

export const getValueFromString = (str) => {
  const valueStart = str?.indexOf("value=") + 6;
  const valueEnd = str?.lastIndexOf("}");
  const value = str.substring(valueStart, valueEnd).trim();
  return value;
};

export const OPTION_VALIDATION_ERRORS = {
  BLANK: "Option values cannot be blank.",
  DUPLICATE: "Duplicate option values are not allowed.",
};

export const validateOptionValues = (values = []) => {
  const normalizedValues = values.map((value) => (value || "").trim());
  const hasBlankOption = normalizedValues.some((value) => value.length === 0);
  const nonBlankValues = normalizedValues.filter((value) => value.length > 0);
  const hasDuplicateOption =
    new Set(nonBlankValues.map((value) => value.toLowerCase())).size !==
    nonBlankValues.length;

  const hasError = hasBlankOption || hasDuplicateOption;
  const optionErrorText = hasBlankOption
    ? OPTION_VALIDATION_ERRORS.BLANK
    : hasDuplicateOption
      ? OPTION_VALIDATION_ERRORS.DUPLICATE
      : "";

  return {
    hasError,
    hasBlankOption,
    hasDuplicateOption,
    optionErrorText,
  };
};

export const translationFormPayloadFormat = (translateForm) => {
  translateForm?.children?.forEach((section) => {
    section?.children?.forEach((child) => {
      checkComponent(child);
      child?.children?.forEach((sub) => {
        checkComponent(sub);
      });
    });
  });
};

const checkComponent = (sub) => {
  if (sub.componentType === "radio" || sub.componentType === "select") {
    if (typeof sub?.options[0] === "string") {
      let options = [];
      const result = sub?.options?.reduce((acc, str) => {
        const id = getIdFromString(str);
        const value = getValueFromString(str);
        acc.push({ id, value });
        return acc;
      }, []);
      result.forEach((item) => {
        options.push({ id: item.id, value: item.value });
      });
      sub.options = options;
    }
  }
};

export const transformFormData = (formData) => {
  const transformedData = {
    ...formData,
    id: null,
    formCode: null,
    version: null,
    lastUpdatedDate: Date.now(),
    formStatus: "Draft",
  };

  TRANSLATION_KEYS_FILTER_LIST.forEach((key) => {
    delete transformedData[key];
  });

  return Object.freeze(transformedData);
};

export const sanitizeTitle = (title, translationFromCode) => {
  const splittedText = title.split(" - ");
  const translationCode = splittedText[splittedText.length - 1];

  if (translationCode.toUpperCase() === translationFromCode.toUpperCase()) {
    splittedText.pop();
    return splittedText.join(" - ");
  }

  return title;
};

export const dataSorter = (data, key, ascending = true) => {
  if (!Array.isArray(data)) return [];
  return [...data].sort((a, b) => {
    return ascending ? (a[key] > b[key] ? 1 : -1) : a[key] < b[key] ? 1 : -1;
  });
};

export const checkIfLatest = (form, latestForm) => {
  const { publishedVersion, version } = form;
  const formVersion = latestForm?.publishedVersion ? publishedVersion : version;
  const latestVersion = latestForm?.publishedVersion ?? latestForm?.version;
  const isVersionLatest = parseFloat(formVersion) >= parseFloat(latestVersion);
  return isVersionLatest;
};

export const checkIfConditionalForm = ({
  formType,
  formCategories,
  locationType,
} = {}) =>
  formType === CONDITIONAL_FORM.CONDITIONAL &&
  locationType?.split(CONDITIONAL_FORM.UNIVERSAL_TAGGING_SPLIT)[0] ===
    "Location Type" &&
  formCategories?.[2]?.categoryName === CONDITIONAL_FORM.RECEIVING &&
  formCategories?.[3]?.categoryName === CONDITIONAL_FORM.RECEIVING;

export const isGeIdTagValidation = (children) => {
  if (!Array.isArray(children) || !children.length) return false;

  const findGeIdTagging = (nodes) => {
    if (!Array.isArray(nodes) || !nodes.length) return null;

    const geIdChild = nodes.find(
      (child) =>
        child?.componentType === "barcode" &&
        child?.uniqueCode?.code === GE_ID_TAG_PROPERTIES.GE_ID,
    );

    if (geIdChild) {
      return Boolean(geIdChild.properties?.geIdTagging);
    }

    for (const child of nodes) {
      if (Array.isArray(child?.children) && child.children.length) {
        const result = findGeIdTagging(child.children);
        if (result !== null) {
          return result;
        }
      }
    }

    return null;
  };

  return findGeIdTagging(children) ?? false;
};

const findGeIdElementId = (children) => {
  if (!Array.isArray(children)) return null;

  for (const child of children) {
    if (
      child.componentType === "barcode" &&
      child.properties?.geIdTagging === true
    ) {
      return child?.elementId;
    }

    if (Array.isArray(child.children) && child.children.length > 0) {
      const result = findGeIdElementId(child.children);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

export const computeGeIdTagging = (form, answers) => {
  const geIdElementId = findGeIdElementId(form.children);
  if (geIdElementId) {
    const geIdTagElement = answers?.children?.find(
      (ans) => ans?.elementId === geIdElementId,
    );
    return geIdTagElement?.answer?.[0]?.answerValue;
  } else {
    return null;
  }
};

export const getOptionalEquipmentIds = async (form) => {
  const getSelection = (children) => {
    if (!Array.isArray(children)) return null;

    for (const child of children) {
      if (
        child?.componentType === COMPONENT_TYPE.DEPENDENT_DROPDOWN &&
        child?.dynamicDropdownList?.[2]?.name === "Turbine Id"
      ) {
        return {
          siteId: child?.answer?.[0]?.siteId ?? null,
          turbineId: child?.answer?.[0]?.turbineId ?? null,
        };
      }

      if (Array.isArray(child?.children) && child.children.length) {
        const nestedSelection = getSelection(child.children);
        if (nestedSelection) {
          return nestedSelection;
        }
      }
    }

    return null;
  };

  const { siteId, turbineId } = getSelection(form?.children) || {
    siteId: form?.siteId ?? null,
    turbineId: form?.turbineId ?? null,
  };

  if (!siteId || !turbineId) {
    return [];
  }

  try {
    const response =
      await formBuilderService.getOptionalEquipmentMapping(siteId);
    const opeData = response?.data || [];

    return opeData
      .filter((item) => item?.turbineId === turbineId)
      .map((item) => item?.optionalEquipmentId ?? item?.optionalEquipId)
      .filter((id) => id != null);
  } catch (error) {
    console.error("Error fetching optional equipment mapping:", error);
    return [];
  }
};

export const getAllQuestionIdsMap = (form) => {
  const allQuestionIdsMap = {};

  const iterateChildrens = (children, parentId) => {
    children?.forEach((child) => {
      let currentParentId = parentId;

      if (
        child?.componentType === SECTION ||
        child?.componentType === SUB_SECTION
      ) {
        currentParentId = child?.elementId;
        allQuestionIdsMap[currentParentId] = [];
      } else {
        allQuestionIdsMap[currentParentId].push(child?.elementId);
      }

      if (child?.children && child?.children?.length) {
        iterateChildrens(child?.children, currentParentId);
      }
    });
  };

  iterateChildrens(form?.children, undefined);

  return allQuestionIdsMap;
};

export const hasPromptEnabledQuestions = (children) => {
  if (!Array.isArray(children)) return false;
  for (const child of children) {
    if (child?.promptId) return true;
    if (Array.isArray(child?.children) && child.children.length) {
      if (hasPromptEnabledQuestions(child.children)) return true;
    }
  }
  return false;
};
